import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEBOUNCE_TIME } from '~/lib/constants';
import { P2GSearchSuggest, P2GText } from '@parcel2go/core-ui';
import { AddressSearchOverview } from './types';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

type AddressSearchSuggestProps = {
  translationNamespace?: string;
  type: 'Origin' | 'Destination';
  results: AddressSearchOverview[];
  onSearch: (query: string, autoSelectSingleResult?: boolean, skip?: number, take?: number) => void;
  onClick: (query: string, address: AddressSearchOverview) => void;
  onLoadMore: (query: string) => void;
  hasMore: boolean;
  loading: boolean;
  errors: Record<string, string>;
  optional?: boolean;
  initial?: string;
};

export function AddressSearchSuggest({
  onClick,
  onSearch,
  results,
  type,
  loading,
  initial,
  errors,
  optional,
  onLoadMore,
  translationNamespace,
  hasMore
}: AddressSearchSuggestProps) {
  const id = `txt-${type}-address-suggest`;
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation([translationNamespace]);
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const [query, setQuery] = useState(initial || '');
  const [realtimeValue, setRealtimeValue] = useState(query);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [focused, setFocused] = useState(false);
  const [entered, setEntered] = useState<Date>();

  const isInError = useMemo(() => !focused && !!errors[id], [errors, focused, id]);

  useEffect(() => {
    if (!optional && inputRef.current) {
      inputRef.current.setAttribute(
        'data-force-error-message',
        t(`ADDRESS_FIELD_${type}_SEARCH_ERROR`.toUpperCase(), { defaultValue: 'Please select an address' })
      );
    }
  }, [inputRef, t, optional]);

  useEffect(() => {
    if (initial) {
      onSearch(initial, false);
    }
  }, []);

  const onUpdated = useCallback(
    (query: string) => {
      setRealtimeValue(query);
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      timeout.current = setTimeout(() => {
        setQuery(query);
        onSearch(query, false);
      }, DEBOUNCE_TIME);
    },
    [onSearch]
  );

  const onSelect = useCallback(
    async (address: AddressSearchOverview) => {
      if (!address) return;
      if (address.identifier === 'loading') return;
      if (address.identifier === 'no-results') return;
      if (address.identifier === 'load-more') {
        inputRef.current?.blur();
        await onLoadMore(query);
        inputRef.current?.focus();
        return;
      }
      inputRef.current?.blur();
      await onClick(query, address);
      inputRef.current?.focus();
    },
    [onClick, query, onLoadMore]
  );

  const onFieldFocus = useCallback(() => {
    setEntered(new Date());
    setFocused(true);
  }, []);

  const onBlurWrapper = useCallback(() => {
    if (entered && id) {
      const time = new Date().getTime() - entered.getTime();
      track({
        type: 'checkout_field_focus',
        payload: {
          ...GenerateBaseAnalyticsPayload([]),
          field: id,
          time
        },
        persist: false
      });
    }
    setFocused(false);
  }, [entered, id, track]);

  const options: AddressSearchOverview[] = useMemo(() => {
    if (loading && results.length === 0) {
      return [
        {
          key: 'loading',
          identifier: 'loading',
          display: t(`ADDRESS_FIELD_${type}_SEARCH_LOADING`.toUpperCase(), { defaultValue: 'Loading...' })
        }
      ];
    }
    if (results.length === 0 && query.length > 0) {
      return [
        {
          key: 'no-results',
          identifier: 'no-results',
          display: t(`ADDRESS_FIELD_${type}_SEARCH_NO_RESULTS`.toUpperCase(), { defaultValue: 'No results found' })
        }
      ];
    }
    const options = results.map((x) => ({ ...x, key: `${x.identifier}_${x.display}` }));
    if (hasMore) {
      options.push({
        key: 'load-more',
        identifier: 'load-more',
        display: t(`ADDRESS_FIELD_${type}_SEARCH_LOAD_MORE`.toUpperCase(), { defaultValue: 'Load more' })
      });
    }
    return options;
  }, [loading, query.length, results, t, type, hasMore]);

  return (
    <P2GSearchSuggest
      onSelect={onSelect}
      options={options}
      autoComplete={type === 'Origin' ? 'shipping postal-code' : 'turn-off'}
      ariaAutoComplete={type === 'Origin' ? undefined : 'none'}
      inputParams={{
        required: !optional,
        inputRef: inputRef,
        id: id,
        dataTestId: `address-suggest-${type}`,
        label: t(`ADDRESS_FIELD_${type}_SEARCH_LABEL`.toUpperCase(), {
          defaultValue: 'Address Search'
        }),
        placeholder: t(`ADDRESS_FIELD_${type}_SEARCH_PLACEHOLDER`.toUpperCase(), {
          defaultValue: 'Enter a postcode'
        }),
        error: isInError,
        errorText: errors[id],
        onFocus: onFieldFocus,
        onBlur: onBlurWrapper,
        value: realtimeValue
      }}
      onUpdate={onUpdated}
      result={(x) => (
        <P2GText
          key={`${x.identifier}_${x.display}`}
          component="p"
          variant="body1"
          dataTestId={`address-suggest-${type}-result-${x.identifier}`}
          data-sentry-mask
        >
          {x.display}
        </P2GText>
      )}
      getOptionLabel={(x) => x.display}
    />
  );
}
